import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../../components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../components/ui/table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CheckCircle, Clock, Edit, MoreHorizontal } from "lucide-react";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import LoggerContext from "../../../../context/LoggerProvider";
import { useLocation, useNavigate } from "react-router-dom";
import useRetryFetch from "../../../../hooks/useRetryFetch";
import { config } from "../../../../Environment";
import { Input } from "../../../../components/ui/input";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../../../components/ui/pagination";
import SingleEngagement from "./singleEngagement";

const itemsPerPage = 5;

const EngagementHistoryModal = ({
  engagementHistoryModal,
  setEngagementHistoryModal,
  prescription,
  setPrescriptions,
  refreshLists,
}: any) => {
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil(prescription?.processes.length / itemsPerPage);

  const { t } = useTranslation();
  const { Logger } = useContext(LoggerContext);
  const location = useLocation();
  const navigate = useNavigate();
  const retryFetch = useRetryFetch();

  const currentEngagements = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return prescription?.processes.slice(startIndex, startIndex + itemsPerPage);
  }, [currentPage, prescription?.processes, itemsPerPage]);

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const range: (number | "...")[] = [];
    const delta = 1;

    const left = Math.max(2, currentPage - delta);
    const right = Math.min(totalPages - 1, currentPage + delta);

    range.push(1);

    if (left > 2) range.push("...");

    for (let i = left; i <= right; i++) {
      range.push(i);
    }

    if (right < totalPages - 1) range.push("...");

    if (totalPages > 1) range.push(totalPages);

    return range;
  };

  const pageNumbers = useMemo(getPageNumbers, [currentPage, totalPages]);

  useEffect(() => {
    setCurrentPage(1);
  }, [engagementHistoryModal]);

  return (
    <>
      <Dialog
        open={engagementHistoryModal}
        onOpenChange={setEngagementHistoryModal}
      >
        <DialogContent className="max-w-4xl px-2 md:px-6">
          <DialogHeader>
            <DialogTitle>{t("engagement_history")}</DialogTitle>
            <DialogDescription>
              {t("engagementHistoryDialogTitle", { name: prescription?.name })}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            <div className="border rounded-md overflow-hidden">
              {/* Single unified table with horizontal scroll  */}
              <div className="overflow-x-auto">
                <Table>
                  {/* Header with sticky positioning */}
                  <TableHeader className="sticky top-0 z-10 bg-background">
                    <TableRow>
                      <TableHead className="min-w-[200px] w-[40%] border-b-[1px]">
                        {t("engagement_name")}
                      </TableHead>
                      <TableHead className="min-w-[120px] w-[20%] hidden md:table-cell">
                        {t("status")}
                      </TableHead>
                      <TableHead className="min-w-[150px] w-[25%] hidden md:table-cell">
                        {t("last_update")}
                      </TableHead>
                      <TableHead className="min-w-fit md:w-[15%] text-right hidden md:table-cell">
                        {t("actions")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>

                  {/* Body with vertical scroll */}
                  <TableBody className="overflow-y-auto h-64">
                    {currentEngagements?.map((process: any) => (
                      <SingleEngagement
                        key={process._id}
                        process={process}
                        refreshLists={refreshLists}
                        setPrescriptions={setPrescriptions}
                      />
                    ))}
                  </TableBody>

                  {/* Footer with pagination - sticky at bottom */}
                  {prescription?.processes?.length > 0 && (
                    <TableFooter className="sticky bottom-0 z-10 bg-background border-t">
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          className="text-right px-0 py-2 border-none"
                        >
                          <Pagination>
                            <PaginationContent className="justify-center space-x-1">
                              {/* Previous */}
                              <PaginationItem>
                                <PaginationPrevious
                                  href="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handlePageChange(currentPage - 1);
                                  }}
                                  aria-disabled={currentPage === 1}
                                />
                              </PaginationItem>

                              {/* Page Numbers (Only on md and above) */}
                              <div className="hidden md:flex">
                                {pageNumbers.map((page, idx) =>
                                  page === "..." ? (
                                    <PaginationItem key={`ellipsis-${idx}`}>
                                      <PaginationEllipsis />
                                    </PaginationItem>
                                  ) : (
                                    <PaginationItem key={page}>
                                      <PaginationLink
                                        href="#"
                                        isActive={currentPage === page}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          handlePageChange(page);
                                        }}
                                      >
                                        {page}
                                      </PaginationLink>
                                    </PaginationItem>
                                  )
                                )}
                              </div>

                              {/* Current Page (visible on mobile) */}
                              {/* <div className="block md:hidden">
                                <PaginationItem>
                                  <span className="px-3 py-1 text-sm text-muted-foreground">
                                    Page {currentPage} of {totalPages}
                                  </span>
                                </PaginationItem>
                              </div> */}

                              {/* Next */}
                              <PaginationItem>
                                <PaginationNext
                                  href="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handlePageChange(currentPage + 1);
                                  }}
                                  aria-disabled={currentPage === totalPages}
                                />
                              </PaginationItem>
                            </PaginationContent>
                          </Pagination>
                        </TableCell>
                      </TableRow>
                    </TableFooter>
                  )}
                </Table>
              </div>

              {/* Empty state message */}
              {prescription?.processes?.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-500 dark:text-gray-400">
                    {t("engagementHistoryDialogEmptyMessage")}
                  </p>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EngagementHistoryModal;
